import { SafetyOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import { Switch, Typography, notification } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';

/**
 * Self-service MFA switch, shared by both apps.  `mutation` is each app's own
 * edit-profile document (editMyProfileForAdmin / editMyProfileForUser); both
 * take the same { profile: { _id, twoFactorEnabled } } shape.
 */
function TwoFactorToggle({ profile, mutation }) {
  const [editProfile] = useMutation(mutation);
  const [saving, setSaving] = useState(false);
  // Optimistic: the redux profile only catches up when the myProfileUpdates
  // subscription lands, so bind the switch to intent and reconcile after.
  const [enabled, setEnabled] = useState(!!profile?.twoFactorEnabled);
  useEffect(() => {
    setEnabled(!!profile?.twoFactorEnabled);
  }, [profile?.twoFactorEnabled]);

  const onChange = useCallback(
    async (checked) => {
      setEnabled(checked);
      setSaving(true);
      try {
        await editProfile({
          variables: {
            profile: { _id: profile._id, twoFactorEnabled: checked },
          },
        });
        notification.success({
          message: 'Saved',
          description: checked
            ? 'Two-factor authentication is now on'
            : 'Two-factor authentication is now off',
        });
      } catch (err) {
        console.error(err);
        setEnabled(!checked);
        notification.error({
          message: 'Error',
          description: 'We apologize.  An error occurred.',
        });
      }
      setSaving(false);
    },
    [profile, editProfile],
  );

  return (
    <div style={{ maxWidth: 600, marginRight: 'auto', marginLeft: 'auto' }}>
      <Typography.Title level={5}>
        <SafetyOutlined /> Two-Factor Authentication
      </Typography.Title>
      <Typography.Paragraph type="secondary">
        When two-factor authentication is on, we email you a 6-digit code each
        time you sign in with your password.
      </Typography.Paragraph>
      <Switch
        checked={enabled}
        loading={saving}
        onChange={onChange}
        checkedChildren="On"
        unCheckedChildren="Off"
      />
    </div>
  );
}

export default TwoFactorToggle;
